import { Link } from "react-router-dom";
import { useState } from "react";
import { useContent } from "@/context/ContentContext";
import { useT } from "@/lib/translations";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Menu,
  Home,
  Dog,
  Heart,
  Calendar,
  Shield,
  Mail,
  Info,
  PawPrint,
} from "lucide-react";

/**
 * Mobile hamburger menu — slide-in drawer from right.
 * Visible only on screens < lg (1024px), matching PublicNav's desktop breakpoint.
 */
export default function MobileNavMenu({ extra = null }) {
  const [open, setOpen] = useState(false);
  const { t } = useContent();
  const L = useT();

  const NAV_LINKS = [
    { to: "/", label: L("Ana Sayfa"), icon: Home, testid: "home" },
    { to: "/canlar", label: L(t("nav.canlar", "Canlarımız")), icon: Dog, testid: "canlar" },
    { to: "/etkinlikler", label: L(t("nav.etkinlikler", "Etkinlikler")), icon: Calendar, testid: "etkinlikler" },
    { to: "/seffaflik", label: L(t("nav.seffaflik", "Şeffaflık")), icon: Shield, testid: "seffaflik" },
    { to: "/#hakkimizda", label: L(t("nav.hakkimizda", "Hakkımızda")), icon: Info, testid: "hakkimizda", external: true },
    { to: "/iletisim", label: L(t("nav.iletisim", "İletişim")), icon: Mail, testid: "iletisim" },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden h-11 w-11"
          aria-label={L("Menüyü aç")}
          data-testid="mobile-menu-trigger"
        >
          <Menu className="h-6 w-6" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[300px] sm:w-[340px] p-0 flex flex-col"
        data-testid="mobile-menu-drawer"
      >
        {/* Hidden title for a11y */}
        <SheetTitle className="sr-only">{L("Ana Menü")}</SheetTitle>

        {/* Brand */}
        <div className="p-6 border-b border-border/50 flex items-center justify-between">
          <Link to="/" onClick={() => setOpen(false)} className="flex items-center gap-2">
            <PawPrint className="h-7 w-7 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl font-semibold">KAHU</span>
          </Link>
        </div>

        {/* Links */}
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV_LINKS.map((item) => {
            const Icon = item.icon;
            const commonProps = {
              onClick: () => setOpen(false),
              className: "flex items-center gap-3 px-6 py-4 text-base hover:bg-muted active:bg-muted transition-colors",
              "data-testid": `mobile-menu-link-${item.testid}`,
            };
            const content = (
              <>
                <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                <span>{item.label}</span>
              </>
            );
            return item.external ? (
              <a key={item.to} href={item.to} {...commonProps}>{content}</a>
            ) : (
              <Link key={item.to} to={item.to} {...commonProps}>{content}</Link>
            );
          })}
          {extra}
        </nav>

        {/* CTA */}
        <div className="p-5 border-t border-border/50">
          <Link to="/canlar" onClick={() => setOpen(false)}>
            <Button className="w-full btn-pill bg-primary text-primary-foreground h-12 text-base">
              <Heart className="w-4 h-4 mr-2" />
              {L("Bir Cana Destek Ol")}
            </Button>
          </Link>
          <p className="text-[11px] text-muted-foreground text-center mt-3">
            {L("Pativista ile birlikte")}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
